import React, { useState } from 'react';
import { Pressable, ScrollView, StyleSheet, View } from 'react-native';

import { AppBottomSheet, EmptyState, Text } from '~/components/core';
import { Ionicons } from '~/components/core/Icons';
import { StrategyEstimated } from '~/api-services/AIStrategyBuilderService/types';
import { formatStat } from '~/constants/strategyConstants';
import { useStyles } from '~/hooks';
import i18n from '~/i18n';
import { FONT_SIZE_S } from '~/constants/typography';
import { scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';

import { STAT_FIELDS, StrategyResultView, withCurrency } from '../../utils/results';

type Props = {
    results: StrategyResultView;
};

export default function StatisticsTab({ results }: Props) {
    const styles = useStyles(stylesGenerator);
    const { estimated, instrumentCurrency, accountCurrency } = results;
    // Which stat's explainer sheet is open (terminology like Sharpe/Sortino/VaR isn't
    // self-evident — every row carries an info affordance).
    const [explaining, setExplaining] = useState<keyof StrategyEstimated | null>(null);

    if (!estimated) {
        return <EmptyState title="ai-strategy-no-stats" />;
    }

    const explainedField = STAT_FIELDS.find((f) => f.key === explaining);

    // The backend's own summary, in account currency. Absent on strategies created before this
    // app started freezing the exchange rate into frontend_json — those rows read "—".
    const signed = (v?: number | null) =>
        typeof v === 'number' && Number.isFinite(v) ? `${v >= 0 ? '+' : ''}${formatStat(v)}` : '—';
    const summaryRows: { labelKey: string; value: string }[] = [
        {
            labelKey: 'ai-strategy-required-funds',
            value:
                typeof results.requiredFunds === 'number'
                    ? withCurrency(formatStat(results.requiredFunds), accountCurrency)
                    : '—',
        },
        {
            labelKey: 'ai-strategy-result-total',
            value:
                typeof results.resultInstrument === 'number'
                    ? withCurrency(signed(results.resultInstrument), instrumentCurrency) +
                      (typeof results.resultAccount === 'number'
                          ? ` (${withCurrency(results.resultAccount.toFixed(2), accountCurrency)})`
                          : '')
                    : '—',
        },
        {
            labelKey: 'ai-strategy-decision-return',
            value: typeof results.resultPercentage === 'number' ? `${results.resultPercentage.toFixed(2)}%` : '—',
        },
    ];

    return (
        <>
            <ScrollView style={styles.container} contentContainerStyle={styles.content}>
                {summaryRows.map(({ labelKey, value }) => (
                    <View key={labelKey} style={styles.row}>
                        <Text style={styles.label}>{labelKey}</Text>
                        <Text noTranslate style={styles.value}>
                            {value}
                        </Text>
                    </View>
                ))}
                {STAT_FIELDS.map(({ key, labelKey, money }) => {
                    const raw = formatStat(estimated[key]);
                    return (
                        <View key={key} style={styles.row}>
                            <Pressable
                                style={({ pressed }) => [styles.labelGroup, pressed && styles.pressed]}
                                onPress={() => setExplaining(key)}
                                hitSlop={6}
                                accessibilityLabel={`${i18n.t(labelKey)} info`}
                            >
                                <Text style={styles.label}>{labelKey}</Text>
                                <Ionicons
                                    name="information-circle-outline"
                                    size={scaleSize(15)}
                                    style={styles.infoIcon}
                                />
                            </Pressable>
                            <Text noTranslate style={styles.value}>
                                {money && raw !== '—' ? withCurrency(raw, instrumentCurrency) : raw}
                            </Text>
                        </View>
                    );
                })}
            </ScrollView>

            <AppBottomSheet
                visible={explaining !== null}
                onClose={() => setExplaining(null)}
                title={explainedField ? i18n.t(explainedField.labelKey) : undefined}
            >
                {explainedField ? <Text style={styles.explainerText}>{`${explainedField.labelKey}-desc`}</Text> : null}
            </AppBottomSheet>
        </>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        container: { flex: 1 },
        content: { padding: scaleSize(14) },
        row: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingVertical: scaleSize(7),
            borderBottomWidth: StyleSheet.hairlineWidth,
            borderBottomColor: theme.tabIconDefault,
        },
        labelGroup: {
            flexDirection: 'row',
            alignItems: 'center',
        },
        pressed: {
            opacity: 0.6,
        },
        label: {
            color: theme.textSecondary,
            fontSize: FONT_SIZE_S,
        },
        infoIcon: {
            color: theme.textSecondary,
            marginLeft: scaleSize(5),
        },
        value: {
            color: theme.text,
            fontSize: FONT_SIZE_S,
            flex: 1,
            textAlign: 'right',
            marginLeft: scaleSize(8),
        },
        explainerText: {
            color: theme.text,
            fontSize: FONT_SIZE_S,
            lineHeight: scaleSize(20),
            paddingBottom: scaleSize(10),
        },
    });
